import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Controller } from "react-hook-form";
import { useState } from "react";
import { cn } from "@/app/_utils/cn";
import { ImagePlus } from "lucide-react";

interface PerformerInputProps {
  control: any;
  errors: any;
}

function TextInput({
  control,
  errors,
  name,
  label,
  placeholder,
  prefix,
}: PerformerInputProps & { name: string; label: string; placeholder: string; prefix?: string }) {
  return (
    <div className="grid gap-3">
      <Label htmlFor={name}>{label}</Label>
      <Controller
        name={name}
        control={control}
        render={({ field }) =>
          prefix ? (
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[13px] text-muted-foreground">
                {prefix}
              </span>
              <Input
                id={name}
                {...field}
                value={field.value ?? ""}
                placeholder={placeholder}
                className="pl-6"
              />
            </div>
          ) : (
            <Input id={name} {...field} value={field.value ?? ""} placeholder={placeholder} />
          )
        }
      />
      {errors[name] && <span className="text-[12px] text-red-600">{errors[name]?.message}</span>}
    </div>
  );
}

export const FirstNameInput = (props: PerformerInputProps) => (
  <TextInput {...props} name="first_name" label="First Name" placeholder="First name" />
);

export const LastNameInput = (props: PerformerInputProps) => (
  <TextInput {...props} name="last_name" label="Last Name" placeholder="Last name" />
);

export const InstagramInput = (props: PerformerInputProps) => (
  <TextInput {...props} name="instagram" label="Instagram" placeholder="comedy.uo" prefix="@" />
);

export const EmailInput = (props: PerformerInputProps) => (
  <TextInput {...props} name="email" label="Email" placeholder="someone@domain.com" />
);

export const PhoneInput = (props: PerformerInputProps) => (
  <TextInput {...props} name="phone" label="Phone" placeholder="3158778177" prefix="+1" />
);

export const ImageInput = ({
  currentImage,
  onSelect,
}: {
  currentImage?: string | null;
  onSelect: (dataUrl: string | null) => void;
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) {
      onSelect(null);
      return;
    }
    if (file.size > 15 * 1000 * 1024) {
      setError("Image is too large (15mb or less)");
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreview(result);
      onSelect(result);
    };
    reader.readAsDataURL(file);
  }

  const displaySrc = preview ?? currentImage;

  return (
    <div className="grid gap-3">
      <Label>Image</Label>
      {displaySrc && (
        <img
          src={displaySrc}
          alt="Performer"
          className="h-[100px] w-full rounded-md border border-border object-contain"
        />
      )}
      <label
        className={cn(
          "flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 text-[12.5px] text-muted-foreground hover:bg-muted",
          error && "border-red-600 text-red-600"
        )}
      >
        <ImagePlus className="size-3.5" />
        Upload image
        <input type="file" accept="image/png, image/jpeg" className="hidden" onChange={handleChange} />
      </label>
      {error && <span className="text-[12px] text-red-600">{error}</span>}
    </div>
  );
};
